// hooks/useUser.ts
import { useState, useEffect, useCallback } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { recordEmailVerified } from '@/lib/emailVerified';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { User } from '@/types/index';
import { auth, db } from '@/lib/firebase';

export const useUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authUser, setAuthUser] = useState<FirebaseUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setAuthUser(firebaseUser);
      setAuthLoading(false);

      // Safety net for the verification timestamp. /auth/action records it when
      // someone clicks the link in a live session, but verifying on another
      // device leaves that page with no session at all — this catches them at
      // their next sign-in. `recordEmailVerified` writes once and never
      // rewrites, so whichever observation lands first is kept and this cannot
      // overwrite the accurate one. See lib/emailVerified.ts.
      if (firebaseUser?.emailVerified) {
        void recordEmailVerified(firebaseUser.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch user data when auth user is available
  useEffect(() => {
    const fetchUserData = async (uid: string) => {
      try {
        setLoading(true);
        console.log('Fetching user data for UID:', uid);

        const userDocRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const user: User = {
            uid: uid,
            name: userData?.name,
            email: userData?.email || authUser?.email || undefined,
            avatar: userData?.avatar || authUser?.photoURL || '/img/icons/icon-profile.svg',
            loggedIn: true,
            eventCreated: userData?.eventCreated,
            stripeLink: userData?.stripeLink,
            stripeAccount: userData?.stripeAccount,
            stripeStatus: userData?.stripeStatus,
            eventPublished: userData?.eventPublished,
            venueManager: userData?.venueManager,
            performingArtist: userData?.performingArtist,
            festivalManager: userData?.festivalManager,
            eventOrganizer: userData?.eventOrganizer,
            promoter: userData?.promoter,
            primaryRole: userData?.primaryRole,
            phoneNumber: userData?.phoneNumber,
            pronouns: userData?.pronouns,
          };
          setUser(user);
        } else {
          // User document doesn't exist yet, create from auth user
          const fallbackUser: User = {
            uid: authUser!.uid,
            name: authUser!.displayName || 'You',
            email: authUser!.email || undefined,
            avatar: authUser!.photoURL || '/img/icons/icon-profile.svg',
            loggedIn: true,
          };
          setUser(fallbackUser);

          // Anonymous sessions get no user document. The artist-profile invite
          // flow signs in anonymously the moment its page loads
          // (app/artistProfileEditor/[inviteId]/layout.tsx), so simply opening
          // an invite link used to create a signed-up-looking user here — no
          // email, and named "You" for want of a displayName. The backend
          // treats a new users doc as a new signup and tried to send a welcome
          // email to null.
          //
          // The account is written for real when the invite is completed
          // (app/artistProfileEditor/[inviteId]/complete/page.tsx), by which
          // point there is a name and an email to write. The local fallback
          // above still stands so the UI has someone to render in the meantime.
          if (authUser!.isAnonymous) return;

          // Create the user document in Firestore
          await setDoc(userDocRef, {
            uid: authUser!.uid,
            name: authUser!.displayName || 'You',
            email: authUser!.email,
            avatar: authUser!.photoURL || '/img/icons/icon-profile.svg',
            loggedIn: true,
          });
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        // Fall back to auth user if Firestore fetch fails
        if (authUser) {
          const fallbackUser: User = {
            uid: authUser.uid,
            name: authUser.displayName || 'You',
            email: authUser.email || undefined,
            avatar: authUser.photoURL || '/img/icons/icon-profile.svg',
            loggedIn: true,
          };
          setUser(fallbackUser);
        } else {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    // Only fetch user data if auth is ready and we have a user
    if (!authLoading) {
      if (authUser?.uid) {
        fetchUserData(authUser.uid);
      } else {
        // No authenticated user, clear user data
        setUser(null);
        setLoading(false);
      }
    }
  }, [authUser, authLoading]);

  const saveUser = useCallback(async (userData: Partial<User>): Promise<boolean> => {
    if (!authUser?.uid) {
      console.error('No authenticated user available for saving');
      return false;
    }

    setLoading(true);

    try {
      const uid = userData.uid || authUser.uid;
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);

      // Filter out undefined values to avoid Firestore error
      const cleanedData = Object.entries(userData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, unknown>);

      if (userDoc.exists()) {
        // Update existing document
        await updateDoc(userDocRef, cleanedData);
      } else {
        // Create new document
        await setDoc(userDocRef, {
          uid,
          ...cleanedData,
        });
      }

      // Update local state if the save was successful
      if (user) {
        setUser({ ...user, ...userData });
      }

      return true;
    } catch (err) {
      console.error("Error saving user:", err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authUser, user]);

  const setAvatar = useCallback((avatarUrl: string) => {
    if (user) {
      const updatedUser = { ...user, avatar: avatarUrl };
      setUser(updatedUser);
      // Optionally auto-save the avatar
      // saveUser(updatedUser);
    }
  }, [user]);

  return {
    user,
    loading: loading || authLoading, // Loading if either auth or user data is loading
    authUser, // Expose auth user in case components need it
    saveUser,
    setAvatar,
  };
};